import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { endpoints } from "../../api/endpoints";
import { api, extractStandardResponseData } from "../../api";
import { RootState } from "../store";
import { PricingList } from "../../shared/interfaces/PricingList";
import { DiscountedPrice } from "../../shared/interfaces/DiscountedPrice";
import { DefaultExemptState } from "../../shared/interfaces/DefaultExemptState";
const reactUsStates = require("react-us-states");

export const pricingListAdapter = createEntityAdapter<PricingList>({
  selectId: (model) => model.id,
  sortComparer: (a, b) => (a.id > b.id ? 1 : 0),
});

export const fetchPricingLists = createAsyncThunk("pricingLists/fetchPricingLists", async () => {
  const {
    data: { data },
  } = await api.get(endpoints.pricingLists);
  return data;
});

export const pricingListUpdate = createAsyncThunk(
  "pricingLists/pricingListUpdate",
  async (pl: PricingList) =>
    api.patch(endpoints.pricingListsUpdate(pl.id), pl).then(extractStandardResponseData)
);

export const pricingListCreate = createAsyncThunk(
  "pricingLists/pricingListCreate",
  async (pl: Partial<PricingList>) =>
    api.post(endpoints.pricingListsCreate, pl).then(extractStandardResponseData)
);

export const pricingListGet = createAsyncThunk("pricingLists/pricingListGet", async (id: number) =>
  api.get(endpoints.pricingListsGet(id)).then(extractStandardResponseData)
);

export const pricingListVariantUpdate = createAsyncThunk(
  "pricingLists/pricingListVariantUpdate",
  async (dp: DiscountedPrice) =>
    api.patch(endpoints.pricingListsVariantUpdate(dp), dp).then(extractStandardResponseData)
);

export const pricingListProductUpdate = createAsyncThunk(
  "pricingLists/pricingListProductUpdate",
  async (dp: DiscountedPrice) =>
    api.patch(endpoints.pricingListsProductUpdate(dp), dp).then(extractStandardResponseData)
);

export const getDefaultCompanyIssues = createAsyncThunk(
  "pricingLists/getDefaultCompanyIssues",
  async (pricingListId: number) => {
    const {
      data: { data },
    } = await api.get(`/pricingLists/${pricingListId}/company_issues`);
    // console.log(data)
    return data;
  }
);

export const getDefaultExemptStates = createAsyncThunk(
  "pricingLists/getDefaultExemptStates",
  async (pricingListId: number) => {
    const {
      data: { data },
    } = await api.get(`/taxExemption/${pricingListId}/tax_exemption_states`);
    console.log(data.length);
    // {data: [{id:1,pricingListId:1,name:"alabama",isExempt:false}]}
    if (!data.length) {
      console.log("Using default states");
      const states = reactUsStates.useStates();
      const defaultStatesExemptions: DefaultExemptState[] = [];
      states.forEach((state: { name: any }) => {
        defaultStatesExemptions.push({ id: 1, stateNameOrCode: state.name, isExempt: false });
      });
      console.log(defaultStatesExemptions);
      return defaultStatesExemptions;
    } else {
      const states = data;
      const defaultStatesExemptions: DefaultExemptState[] = [];
      states.forEach((state: { stateNameOrCode: string; isExempt: boolean }, index: number) => {
        defaultStatesExemptions.push({
          id: index,
          stateNameOrCode: state.stateNameOrCode,
          isExempt: state.isExempt,
        });
      });
      console.log(defaultStatesExemptions);
      return defaultStatesExemptions;
    }
  }
);

export const getDefaultExemptStatus = createAsyncThunk(
  "pricingLists/getDefaultExemptStatus",
  async (pricingListId: number) => {
    const {
      data: { data },
    } = await api.get(`/taxExemption/${pricingListId}/tax_exemption_status`);
    // console.log({data})
    // {data: [{id:1,pricingListId:1,name:"alabama",isExempt:false}]}
    return data;
  }
);

export const clientUpdateTaxExemption = createAsyncThunk(
  "pricingLists/getDefaultExemptStates",
  async (state: boolean) => state
  // api.post(endpoints.clientUpdate(client.id), client).then(extractStandardResponseData)
);

export const updateDefaultCompanyIssues = createAsyncThunk(
  "pricingLists/updateDefaultCompanyIssues",
  async (payload: { pricingListId: number; data: any }) => {
    const {
      data: { data },
    } = await api.patch(`/pricingLists/${payload.pricingListId}/company_issues`, payload.data);
    return data;
  }
);

export const updateDefaultTaxExemption = createAsyncThunk(
  "pricingLists/updateDefaultTaxExemption",
  async (payload: { pricingListId: number; data: any; isTaxExempt: boolean }) => {
    console.log({ payload });
    const {
      data: { data },
    } = await api.patch(`/taxExemption/${payload.pricingListId}/tax_exemption`, payload);
    return data;
  }
);

export const addProductToDefaultCompanyIssues = createAsyncThunk(
  "pricingLists/addProductToDefaultCompanyIssues",
  async (payload: { pricingListId: number; productId: number }) => {
    const {
      data: { data },
    } = await api.patch(
      `/pricingLists/${payload.pricingListId}/company_issues/products/${payload.productId}`
    );
    return data;
  }
);

export const removeProductFromDefaultCompanyIssues = createAsyncThunk(
  "pricingLists/removeProductFromDefaultCompanyIssues",
  async (payload: { pricingListId: number; productId: number }) => {
    const {
      data: { data },
    } = await api.delete(
      `/pricingLists/${payload.pricingListId}/company_issues/products/${payload.productId}`
    );
    return data;
  }
);

export const resetDefaultCompanyIssues = createAsyncThunk(
  "pricingLists/resetDefaultCompanyIssues",
  async (pricingListId: number) => {
    const {
      data: { data },
    } = await api.post(`/pricingLists/${pricingListId}/company_issues/reset`);
    return data;
  }
);

export const resetDefaultTaxExemption = createAsyncThunk(
  "pricingLists/resetDefaultCompanyIssues",
  async (pricingListId: number) => {
    const {
      data: { data },
    } = await api.post(`/taxExemption/${pricingListId}/tax_exemption/reset`);
    return data;
  }
);

const pricingListsSlice = createSlice({
  name: "pricingLists",
  initialState: pricingListAdapter.getInitialState({
    loading: {
      fetchPricingLists: false,
      pricingListUpdate: false,
      pricingListCreate: false,
      pricingListGet: false,
      pricingListVariantUpdate: false,
      pricingListProductUpdate: false,
      getDefaultCompanyIssues: false,
    },
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPricingLists.pending, (state) => {
        state.loading.fetchPricingLists = true;
      })
      .addCase(fetchPricingLists.fulfilled, (state, action) => {
        pricingListAdapter.setAll(state, action.payload);
        state.loading.fetchPricingLists = false;
      })
      .addCase(pricingListUpdate.pending, (state) => {
        state.loading.pricingListUpdate = true;
      })
      .addCase(pricingListUpdate.fulfilled, (state, action) => {
        pricingListAdapter.upsertOne(state, action.payload);
        state.loading.pricingListUpdate = false;
      })
      .addCase(pricingListCreate.pending, (state) => {
        state.loading.pricingListCreate = true;
      })
      .addCase(pricingListCreate.fulfilled, (state, action) => {
        pricingListAdapter.addOne(state, action.payload);
        state.loading.pricingListCreate = false;
      })
      .addCase(pricingListGet.pending, (state) => {
        state.loading.pricingListGet = true;
      })
      .addCase(pricingListGet.fulfilled, (state, action) => {
        pricingListAdapter.upsertOne(state, action.payload);
        state.loading.pricingListGet = false;
      })
      .addCase(pricingListVariantUpdate.pending, (state) => {
        state.loading.pricingListVariantUpdate = true;
      })
      .addCase(pricingListVariantUpdate.fulfilled, (state, action) => {
        pricingListAdapter.upsertOne(state, action.payload);
        state.loading.pricingListVariantUpdate = false;
      })
      .addCase(pricingListProductUpdate.pending, (state) => {
        state.loading.pricingListProductUpdate = true;
      })
      .addCase(pricingListProductUpdate.fulfilled, (state, action) => {
        pricingListAdapter.upsertOne(state, action.payload);
        state.loading.pricingListProductUpdate = false;
      })
      .addCase(getDefaultCompanyIssues.pending, (state) => {
        state.loading.getDefaultCompanyIssues = true;
      })
      .addCase(getDefaultCompanyIssues.fulfilled, (state) => {
        state.loading.getDefaultCompanyIssues = false;
      });
  },
});

export const pricingListsActions = pricingListsSlice.actions;
export const pricingListsReducers = pricingListsSlice.reducer;
export const pricingListSelectors = pricingListAdapter.getSelectors(
  (s: RootState) => s.pricingLists
);
